import apiClient from '@/lib/apiClient';
import { ApiResponse } from '@/types';

// Mirrors the server DashboardMetrics (services/salesDashboard.service.ts). Every
// figure is aggregated server-side via GROUP BY (bug 7), so the dashboard renders
// straight from this payload instead of reducing the whole lead table client-side.
export interface DashboardMetrics {
  totals: { leads: number; clients: number; futureClients: number; revenue: number };
  tcStatus: { confirmed: number; notConfirmed: number; notSent: number };
  leadStatus: { lead: number; futureClient: number; client: number };
  paymentStatus: { full: number; partial: number; notPaid: number };
  products: Array<{ code: string; name: string; units: number; revenue: number }>;
  states: Array<{
    state: string;
    leads: number;
    clients: number;
    products: number;
    revenue: number;
  }>;
  cities: Array<{
    city: string;
    state: string;
    leads: number;
    clients: number;
    products: number;
    revenue: number;
  }>;
  timeSeries: Array<{
    label: string;
    leads: number;
    clients: number;
    revenue: number;
    sortKey: string;
  }>;
  paymentCollection: {
    collected: number;
    expected: number;
    outstanding: number;
    collectedPct: number;
    fullPaidCount: number;
    fullPaidAmount: number;
    partialCount: number;
    partialOwed: number;
    notPaidCount: number;
    notSentOwed: number;
  };
}

// Pending actions are their own query/window so the pending-work filter doesn't
// refetch the whole dashboard (review #2).
export interface PendingAction {
  id: number;
  code: number;
  name: string;
  reason: string;
  ageDays: number;
}

export interface DashboardWindowParams {
  from: string;
  to: string;
}

// Revenue toward the monthly/yearly targets for FIXED current-period windows
// (this month, this year), independent of the dashboard's date filter.
export interface TargetProgress {
  monthRevenue: number;
  yearRevenue: number;
}

export const salesDashboardService = {
  getMetrics: async (params: DashboardWindowParams): Promise<DashboardMetrics> => {
    const { data } = await apiClient.get<ApiResponse<DashboardMetrics>>(
      '/sales/dashboard-metrics',
      { params },
    );
    if (!data.data) throw new Error('Dashboard metrics returned no data');
    return data.data;
  },

  getPending: async (params: DashboardWindowParams): Promise<PendingAction[]> => {
    const { data } = await apiClient.get<ApiResponse<PendingAction[]>>('/sales/dashboard-pending', {
      params,
    });
    return data.data ?? [];
  },

  getTargetProgress: async (params: {
    monthStart: string;
    yearStart: string;
  }): Promise<TargetProgress> => {
    const { data } = await apiClient.get<ApiResponse<TargetProgress>>('/sales/target-progress', {
      params,
    });
    return data.data ?? { monthRevenue: 0, yearRevenue: 0 };
  },
};
